import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApplyRecordWrites } from '@/entities/taxonomy-dataset';
import { track } from '@/shared/analytics';
import { get } from './lookup';
import type {
  RecordWrite,
  TaxonomyRecord,
  TaxonomyRecordCells,
  TaxonomyRecordOutputs,
} from '@/entities/taxonomy-dataset';

/**
 * The grid's unsaved edits, and the explicit Save that lands them.
 *
 * Deliberately NOT `useAutosaveDraft`. That hook is an 800 ms value-debounce
 * built for the schema builder, where a draft version is the unit of work; here
 * the unit is a batch of records the user reviews before committing, which is
 * how Claravine's submission-then-commit and Retool's changeset both work. Two
 * autosaves already exist in this module and unifying them was considered and
 * rejected; this is a third model, on purpose.
 *
 * The cost of explicit Save is that leaving with unsaved work silently discards
 * it — the prototype's worst bug. So the navigation guard below is part of this
 * hook rather than something a page remembers to add.
 */

/** A per-record patch of only the cells that changed. */
type DirtyPatches = Record<string, TaxonomyRecordCells>;

export interface RecordDraft {
  /** Records with pending edits merged in, in grid order. */
  rows: TaxonomyRecord[];
  dirtyIds: Set<string>;
  isDirty: boolean;
  saving: boolean;
  /** Ids the last save refused because their revision had moved on. */
  conflictedIds: string[];
  setCells: (writes: CellWrite[]) => void;
  /**
   * Forgets the named records' unsaved edits.
   *
   * For a server-side write the draft did not make — Clear. A patch is merged
   * over whatever the refetch returns, so without this the cleared cell
   * re-displayed the edit and the next Save wrote it back: from the user's side,
   * Clear did nothing.
   */
  dropLocalEdits: (recordIds: string[]) => void;
  /**
   * Shows the conflict banner for a refusal this hook did not make.
   *
   * Clear is a different endpoint returning the same `{ applied, conflicted }`
   * envelope, and it was discarding the `conflicted` half — so a Clear refused
   * because the row had moved on told the user nothing at all, while the very
   * same refusal on Save explained itself. One banner, both paths.
   *
   * MERGES rather than replaces. Two paths now write this state, and a refused
   * Clear that replaced it erased a refused Save's still-unresolved conflicts —
   * those records keep their stale patches and will 409 again, so dropping them
   * from the banner told the user a problem had resolved itself.
   */
  reportConflicts: (recordIds: string[]) => void;
  /**
   * Persists the pending edits, plus `extra` if given.
   *
   * `extra` exists for values produced OUTSIDE the grid that must not sit in an
   * unsaved draft — a minted tracking code is the case: it is already spent
   * server-side, so leaving it discardable makes the grid disagree with what the
   * schema has issued. Passing it here rather than calling `setCells` and then
   * `save` avoids reading `patches` before React has applied that update.
   */
  /**
   * Persists the draft, leaving `holdBack` records alone.
   *
   * The caller owns the reason for withholding; the hook only honours the set.
   */
  save: (extra?: CellWrite[], holdBack?: ReadonlySet<string>) => void;
}

export interface CellWrite {
  recordId: string;
  fieldId: string;
  value: string | string[] | null;
}

export interface UseRecordDraftOptions {
  datasetId: string;
  records: readonly TaxonomyRecord[];
  /**
   * Recomputes a record's assembled strings from its merged cells. Supplied by
   * the caller because assembly needs the schema, and this hook only knows
   * about records.
   */
  assemble: (cells: TaxonomyRecordCells) => {
    assembledName: string | null;
    outputs: TaxonomyRecordOutputs;
  };
}

export function useRecordDraft({
  datasetId,
  records,
  assemble,
}: UseRecordDraftOptions): RecordDraft {
  const [patches, setPatches] = useState<DirtyPatches>({});
  const [conflictedIds, setConflictedIds] = useState<string[]>([]);
  const apply = useApplyRecordWrites(datasetId);

  /**
   * Read inside the guard rather than closed over, because the listener outlives
   * the render that installed it — the same reason `useAutosaveDraft` keeps its
   * draft in a ref.
   */
  const dirtyRef = useRef(false);

  /**
   * The assembled result of one record's patch, cached on the patch object.
   *
   * `assemble` runs the whole rule engine, and it ran for EVERY dirty record on
   * every keystroke — so the grid's own per-row cache fixed the untouched rows
   * and left the dirty ones, which a paste or a bulk duplicate can make hundreds
   * of. `setCells` only replaces the edited record's own patch object, so keying
   * on that object means one evaluation per keystroke rather than one per dirty
   * row.
   *
   * A fresh map whenever `records` or `assemble` changes, which is what makes a
   * cached entry safe to trust.
   */
  const assembledCache = useMemo(
    () =>
      new WeakMap<
        TaxonomyRecordCells,
        { assembledName: string | null; outputs: TaxonomyRecordOutputs }
      >(),
    // The factory reads neither; they are here BECAUSE they invalidate what the map holds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [records, assemble],
  );

  const rows = useMemo(
    () =>
      records.map((record) => {
        const patch = get(patches, record.id);
        if (!patch) {
          return record;
        }
        const cells = { ...record.cells, ...patch };
        const cached = assembledCache.get(patch);
        const assembled = cached ?? assemble(cells);
        if (!cached) {
          assembledCache.set(patch, assembled);
        }
        return { ...record, cells, ...assembled };
      }),
    [records, patches, assemble, assembledCache],
  );

  /**
   * Only patches for records that still exist.
   *
   * A patch keyed to a deleted record could never be sent — `save()` walks
   * `records`, finds nothing, and early-returns — so the Save button stayed
   * enabled doing nothing and the navigation blocker fired on every exit with no
   * way to satisfy it. Reachable in two clicks: edit a row, then delete it.
   */
  const liveIds = useMemo(() => new Set(records.map((r) => r.id)), [records]);
  const dirtyIds = useMemo(
    () => new Set(Object.keys(patches).filter((id) => liveIds.has(id))),
    [patches, liveIds],
  );
  const isDirty = dirtyIds.size > 0;
  dirtyRef.current = isDirty;

  /**
   * Warns before the tab closes or reloads with unsaved records.
   *
   * Covers only the browser-level exit. In-app navigation is the router's to
   * block, and `TaxonomyRecordGrid` mounts a `useBlocker` for it — a
   * `beforeunload` handler never fires on a client-side route change.
   */
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (dirtyRef.current) {
        event.preventDefault();
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const setCells = useCallback((writes: CellWrite[]) => {
    if (writes.length === 0) {
      return;
    }
    // One functional update for the whole batch: a paste is hundreds of cells,
    // and per-cell updates in one tick would each build from the same stale
    // snapshot and drop all but the last.
    setPatches((current) => {
      const next = { ...current };
      for (const { recordId, fieldId, value } of writes) {
        next[recordId] = { ...(next[recordId] ?? {}), [fieldId]: value };
      }
      return next;
    });
  }, []);

  useEffect(() => {
    setPatches((current) => {
      const kept = Object.entries(current).filter(([id]) => liveIds.has(id));
      return kept.length === Object.keys(current).length ? current : Object.fromEntries(kept);
    });
  }, [liveIds]);

  const mergeConflicts = useCallback((recordIds: string[]) => {
    setConflictedIds((current) => [...new Set([...current, ...recordIds])]);
  }, []);

  const dropLocalEdits = useCallback((recordIds: string[]) => {
    const dropped = new Set(recordIds);
    setPatches((current) => {
      const kept = Object.entries(current).filter(([id]) => !dropped.has(id));
      return kept.length === Object.keys(current).length ? current : Object.fromEntries(kept);
    });
    setConflictedIds((current) => current.filter((id) => !dropped.has(id)));
  }, []);

  /**
   * Persists the draft.
   *
   * `holdBack` names records to leave alone. The caller owns the reason — the grid
   * withholds rows that would be refused, either for a missing required value or
   * for a name another row already has — and the point is that everything ELSE
   * still saves. Refusing the whole draft because one row is unfit meant fifty
   * correct edits became unsavable behind the fifty-first, with the navigation
   * blocker (which offers no Save) as the only exit.
   *
   * Held-back records stay dirty and keep their markers, so nothing is lost and
   * the reason stays on screen.
   */
  const save = useCallback(
    (extra: CellWrite[] = [], holdBack: ReadonlySet<string> = new Set()) => {
      /**
       * `extra` merged here rather than through `setCells` first, because state
       * updates are not applied synchronously — a save reading `patches` in the
       * same tick would send the pending edits and silently drop the extras.
       */
      const effective: DirtyPatches = { ...patches };
      for (const { recordId, fieldId, value } of extra) {
        effective[recordId] = { ...(effective[recordId] ?? {}), [fieldId]: value };
      }

      const writes: RecordWrite[] = [];
      let heldBackCount = 0;
      for (const record of records) {
        const patch = get(effective, record.id);
        if (!patch) {
          continue;
        }
        if (holdBack.has(record.id)) {
          heldBackCount += 1;
          continue;
        }
        const { assembledName, outputs } = assemble({ ...record.cells, ...patch });
        writes.push({
          id: record.id,
          patch,
          // An empty name is stored as NULL so the partial unique index ignores
          // it, which is what lets a grid hold many half-filled rows.
          assembledName: assembledName || null,
          outputs,
          expectedRevision: record.revision,
        });
      }

      // Shown immediately, and BEFORE the early return: an extra naming a record
      // this grid no longer holds would otherwise vanish with no error at all.
      // The success handler removes these keys again once they land.
      if (extra.length > 0) {
        setCells(extra);
      }

      if (writes.length === 0) {
        return;
      }

      // The VALUES sent, not just the keys. A key alone cannot tell a landed
      // edit from one the user retyped while the request was in flight.
      const sentPatches = new Map(writes.map((w) => [w.id, w.patch]));

      apply.mutate(writes, {
        onSuccess: (result) => {
          setConflictedIds(result.conflicted);
          /*
           * Emitted here rather than inside the mutation, which cannot see the
           * hold-back: it is handed the surviving writes and has no idea any row
           * was withheld. Reported from the mutation, `saved_count` silently
           * narrowed from "rows the user asked to save" to "the fit subset", so a
           * 51-row save that dropped one was indistinguishable from a clean 50.
           *
           * On the PER-CALL handler, which carries one known cost: TanStack gates
           * these on `hasListeners()`, so if the grid unmounts before the response
           * lands the event is dropped. The write still succeeds and the mutation's
           * own `onSuccess` still patches the cache - only the telemetry is lost,
           * and it is lost for exactly the long saves it is most interesting for.
           *
           * Accepted rather than fixed, because the alternatives are worse: the
           * mutation cannot see the hold-back at all, and threading the count into
           * its variables would reshape an `entities/` API for telemetry, which
           * `apps/agent-studio/AGENTS.md` tells us not to do.
           *
           * Overlap is a separate question and is not a concern: Save is disabled
           * while `draft.saving`, so a newer set of options cannot replace an
           * in-flight one.
           */
          track('taxonomy_records_saved', {
            dataset_id: datasetId,
            saved_count: result.applied.length,
            conflicted_count: result.conflicted.length,
            held_back_count: heldBackCount,
          });
          /**
           * Drop only the KEYS that were sent and landed — not the record's whole
           * patch entry.
           *
           * Editing is not blocked while a save is in flight (only the button is),
           * so a cell touched during the round trip is in `patches` but was never
           * transmitted. Deleting the entry wholesale threw that edit away
           * silently: it was neither saved nor still pending.
           *
           * A conflicted record keeps everything, so the user can still see what
           * they were doing before deciding to reload it.
           */
          const landed = new Set(result.applied.map((a) => a.id));
          setPatches((current) => {
            const next: DirtyPatches = {};
            for (const [id, patch] of Object.entries(current)) {
              if (!landed.has(id)) {
                next[id] = patch;
                continue;
              }
              /**
               * A key is cleared only when its value still MATCHES what was
               * sent. Clearing by key alone deleted an edit the user made to the
               * same cell during the round trip: the key matched, so the newer
               * value was dropped without ever having been transmitted — the
               * same silent loss as dropping the whole entry, one cell narrower
               * and correspondingly harder to notice.
               */
              const sent = sentPatches.get(id) ?? {};
              const remaining = Object.fromEntries(
                Object.entries(patch).filter(
                  ([fieldId, value]) => !(fieldId in sent) || !sameCell(sent[fieldId], value),
                ),
              );
              if (Object.keys(remaining).length > 0) {
                next[id] = remaining;
              }
            }
            return next;
          });
        },
      });
    },
    [records, patches, assemble, apply, setCells, datasetId],
  );

  return {
    rows,
    dirtyIds,
    isDirty,
    saving: apply.isPending,
    conflictedIds,
    setCells,
    dropLocalEdits,
    reportConflicts: mergeConflicts,
    save,
  };
}

/** Cell equality, aware that a multiselect holds an array. */
function sameCell(
  a: string | string[] | null | undefined,
  b: string | string[] | null | undefined,
): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    const left = Array.isArray(a) ? a : [];
    const right = Array.isArray(b) ? b : [];
    return left.length === right.length && left.every((v, i) => v === right[i]);
  }
  return a === b;
}
